<template>
  <div class="pdp-set-picker">
    <div class="pdp-set-picker__header">
      <div class="pdp-set-picker__header-row">
        <div class="pdp-set-picker__title">
          Building a PDP image set for <b>{{ productLabel || 'your product' }}</b>.
        </div>
        <div class="pdp-set-picker__bulk">
          <button
            type="button"
            class="pdp-set-picker__bulk-btn"
            :disabled="disabled"
            @click="setAll('ai')"
          >
            <i class="bi bi-stars" /> All AI
          </button>
          <span class="pdp-set-picker__bulk-sep">·</span>
          <button
            type="button"
            class="pdp-set-picker__bulk-btn"
            :disabled="disabled || !anyTemplatesAvailable"
            @click="setAll('gallery')"
          >
            <i class="bi bi-image" /> All Gallery
          </button>
        </div>
      </div>
      <p
        v-if="referenceMode"
        class="pdp-set-picker__subtitle"
      >
        One card per reference image — each
        <b><i class="bi bi-stars pdp-set-picker__intro-icon" /> AI</b>
        card shows the source it'll be based on. Reorder, remove, or add cards, then
        create your set.
      </p>
    </div>

    <div class="pdp-set-picker__controls">
      <div
        v-if="!referenceMode"
        class="pdp-set-picker__preset"
        :class="{ 'is-open': presetOpen }"
      >
        <button
          type="button"
          class="pdp-set-picker__preset-trigger"
          :disabled="disabled"
          @click="togglePresetDropdown"
        >
          <i class="bi bi-soundwave pdp-set-picker__preset-icon" />
          <span class="pdp-set-picker__preset-value">{{ activePresetLabel || 'Sequence preset...' }}</span>
          <i class="bi bi-chevron-down pdp-set-picker__preset-chevron" />
        </button>
        <div
          v-show="presetOpen"
          class="pdp-set-picker__preset-menu"
        >
          <button
            v-for="src in presetSources"
            :key="src.id"
            type="button"
            class="pdp-set-picker__preset-item"
            :class="{ 'is-active': src.id === activePresetId }"
            @click="selectPreset(src.id)"
          >
            <span>{{ src.label }} <span class="pdp-set-picker__preset-count">· {{ src.count }}</span></span>
            <i
              v-if="src.id === activePresetId"
              class="bi bi-check-lg"
            />
          </button>
        </div>
      </div>
      <span
        v-if="!referenceMode"
        class="pdp-set-picker__or"
      >or</span>
      <div class="pdp-set-picker__reference">
        <i class="bi bi-search pdp-set-picker__reference-icon" />
        <input
          type="text"
          class="pdp-set-picker__reference-input"
          placeholder="Reference: ASIN, Salsify ID, or product name..."
          :value="referenceInput"
          :disabled="disabled"
          @input="referenceInput = $event.target.value"
          @keydown.enter.prevent="submitReference"
        >
      </div>

      <div
        v-if="addableTypes.length"
        class="pdp-set-picker__add-image-type"
      >
        <button
          type="button"
          class="pdp-set-picker__add-trigger"
          :class="{ 'is-open': addableTypesOpen }"
          :disabled="disabled"
          @click="toggleAddableTypesDropdown"
        >
          <i class="bi bi-plus-lg" /> Add image type
          <span>({{ addableTypes.length }})</span>
          <i class="bi bi-chevron-down pdp-set-picker__add-chevron" />
        </button>
        <div
          v-if="addableTypesOpen"
          class="pdp-set-picker__add-dropdown-menu"
        >
          <button
            v-for="t in addableTypes"
            :key="t.key"
            type="button"
            class="pdp-set-picker__add-item"
            @click="addRow(t)"
          >
            + {{ t.label }}
          </button>
        </div>
      </div>

      <!-- Sessions staged from a Creative Brief keep the brief one click away:
           read it in a drawer, or jump to the brief itself. (POR-13996) -->
      <div
        v-if="briefId"
        class="pdp-set-picker__brief"
      >
        <button
          type="button"
          class="pdp-set-picker__brief-btn"
          :title="`From brief: ${briefLabel}`"
          @click="$emit('open-brief')"
        >
          <i class="bi bi-file-earmark-text" />
          <span class="pdp-set-picker__brief-name">From brief: {{ briefLabel }}</span>
        </button>
        <button
          type="button"
          class="pdp-set-picker__brief-open"
          aria-label="Open the brief"
          title="Open the brief"
          @click="$emit('open-brief-full')"
        >
          <i class="bi bi-box-arrow-up-right" />
        </button>
      </div>
    </div>

    <div
      ref="cardsList"
      class="pdp-set-picker__grid"
    >
      <div
        v-for="(row, rowIdx) in localRows"
        :key="row._uid"
        class="pdp-set-picker__card"
        :class="{
          'is-reference': !!row.reference_image_url,
          'is-popover-open': galleryOpenIdx === rowIdx || directionOpenIdx === rowIdx || yoursMenuIdx === rowIdx || cardMenuIdx === rowIdx,
        }"
      >
        <div
          v-if="!disabled"
          class="pdp-set-picker__card-tools"
        >
          <template v-if="isHeroRow(row)">
            <button
              type="button"
              class="pdp-set-picker__mode-btn"
              :class="{ 'is-active': row.mode === 'original' }"
              title="Original — use the product image as-is"
              @click.stop="pickMode(rowIdx, 'original')"
            >
              <i class="bi bi-image-fill" />
            </button>
            <button
              type="button"
              class="pdp-set-picker__mode-btn"
              :class="{ 'is-active': row.mode === 'ai' }"
              title="AI — generate a fresh image"
              @click.stop="pickMode(rowIdx, 'ai')"
            >
              <i class="bi bi-stars" />
            </button>
            <button
              type="button"
              class="pdp-set-picker__mode-btn"
              :class="{ 'is-active': row.mode === 'yours' }"
              title="Use your own image (upload, File Browser, or Salsify)"
              @click.stop="toggleYoursMenu(rowIdx)"
            >
              <i class="bi bi-upload" />
            </button>
          </template>
          <template v-else>
            <button
              type="button"
              class="pdp-set-picker__mode-btn"
              :class="{ 'is-active': row.mode === 'ai' }"
              title="AI — generate a fresh image"
              @click.stop="pickMode(rowIdx, 'ai')"
            >
              <i class="bi bi-stars" />
            </button>
            <button
              v-if="hasTemplates(row)"
              type="button"
              class="pdp-set-picker__mode-btn"
              :class="{ 'is-active': row.mode === 'gallery', 'is-open': galleryOpenIdx === rowIdx }"
              title="Gallery — pick a pre-designed template"
              @click.stop="toggleGallery(rowIdx)"
            >
              <i class="bi bi-image" />
            </button>
            <button
              v-if="row.type === 'lifestyle'"
              type="button"
              class="pdp-set-picker__mode-btn"
              :class="{ 'is-active': row.mode === 'scene' }"
              title="Scene — composite into a lifestyle scene"
              @click.stop="pickMode(rowIdx, 'scene')"
            >
              <i class="bi bi-image-alt" />
            </button>
            <button
              type="button"
              class="pdp-set-picker__mode-btn"
              :class="{ 'is-active': row.mode === 'yours' }"
              title="Use your own image (upload, File Browser, or Salsify)"
              @click.stop="toggleYoursMenu(rowIdx)"
            >
              <i class="bi bi-upload" />
            </button>
          </template>
        </div>

        <div class="pdp-set-picker__card-tools-right">
          <button
            v-if="isGenerativeRow(row) && rowEditable(rowIdx)"
            type="button"
            class="pdp-set-picker__wand"
            :class="{ 'is-active': hasDirection(row, rowIdx), 'is-open': directionOpenIdx === rowIdx }"
            title="Add design direction (optional)"
            @click.stop="toggleDirection(rowIdx, $event)"
          >
            <i class="bi bi-magic" />
            <span
              v-if="hasDirection(row, rowIdx)"
              class="pdp-set-picker__wand-dot"
            />
          </button>
          <button
            v-if="!disabled"
            type="button"
            class="pdp-set-picker__card-menu"
            :class="{ 'is-open': cardMenuIdx === rowIdx }"
            title="More actions"
            :disabled="disabled"
            :aria-label="`More actions for ${row.label}`"
            aria-haspopup="menu"
            :aria-expanded="cardMenuIdx === rowIdx ? 'true' : 'false'"
            @click.stop="toggleCardMenu(rowIdx, $event)"
          >
            <i class="bi bi-three-dots-vertical" />
          </button>
        </div>

        <div
          v-if="cardMenuIdx === rowIdx"
          class="pdp-set-picker__card-menu-pop"
          :class="{ 'is-above': cardMenuAbove[rowIdx] }"
          role="menu"
          @click.stop
        >
          <button
            type="button"
            class="pdp-set-picker__card-menu-item"
            role="menuitem"
            :disabled="disabled"
            @click="duplicateRow(rowIdx)"
          >
            <i class="bi bi-copy" /> Duplicate slot
          </button>
          <button
            type="button"
            class="pdp-set-picker__card-menu-item is-danger"
            role="menuitem"
            :disabled="disabled"
            @click="removeRowFromMenu(rowIdx)"
          >
            <i class="bi bi-x-lg" /> Remove
          </button>
        </div>

        <div
          v-if="galleryOpenIdx === rowIdx"
          class="pdp-set-picker__gallery-pop"
          @click.stop
        >
          <div class="pdp-set-picker__gallery-pop-head">
            Choose a {{ row.label }} template
          </div>
          <div class="pdp-set-picker__gallery-pop-grid">
            <div
              v-for="tpl in templatesForRow(row)"
              :key="`${row.rowIndex}-${tpl.id}`"
              class="pdp-set-picker__gallery-pop-tile"
              :class="{ 'is-selected': row.mode === 'gallery' && row.template_id === tpl.id }"
              role="button"
              :tabindex="disabled ? -1 : 0"
              @click="onGalleryPick(rowIdx, tpl)"
              @keydown.enter.prevent="onGalleryPick(rowIdx, tpl)"
            >
              <img
                :src="tpl.thumbnail"
                :alt="tpl.name"
              >
              <i
                v-if="row.mode === 'gallery' && row.template_id === tpl.id"
                class="bi bi-check-circle-fill pdp-set-picker__gallery-pop-check"
              />
              <button
                type="button"
                class="pdp-set-picker__gallery-pop-expand"
                aria-label="Preview template"
                @click.stop="openLightbox(rowIdx, tpl)"
              >
                <i class="bi bi-arrows-angle-expand" />
              </button>
            </div>
          </div>
          <button
            type="button"
            class="pdp-set-picker__gallery-pop-browse"
            @click="$emit('open-gallery')"
          >
            Browse full gallery →
          </button>
        </div>

        <div
          v-if="yoursMenuIdx === rowIdx"
          class="pdp-set-picker__yours-menu"
          @click.stop
        >
          <div class="pdp-set-picker__yours-menu-head">
            USE YOUR OWN IMAGE
          </div>
          <button
            type="button"
            class="pdp-set-picker__yours-menu-item"
            :disabled="disabled"
            @click="requestOwnImage(rowIdx, 'upload')"
          >
            <i class="bi bi-upload" /> Upload from computer...
          </button>
          <button
            type="button"
            class="pdp-set-picker__yours-menu-item"
            :disabled="disabled"
            @click="requestOwnImage(rowIdx, 'browser')"
          >
            <i class="bi bi-folder2-open" /> Choose from File Browser...
          </button>
          <button
            type="button"
            class="pdp-set-picker__yours-menu-item"
            :disabled="disabled"
            @click="requestOwnImage(rowIdx, 'salsify')"
          >
            <i class="bi bi-bar-chart-steps" /> Pull from Salsify...
          </button>
        </div>

        <div
          v-if="directionOpenIdx === rowIdx"
          class="pdp-set-picker__direction"
          :class="{ 'is-above': directionAbove[rowIdx], 'is-left': directionLeftAlign[rowIdx] }"
          @click.stop
        >
          <div class="pdp-set-picker__direction-head">
            <span class="pdp-set-picker__direction-title">Customize {{ row.label }}</span>
            <span class="pdp-set-picker__direction-optional">All optional</span>
          </div>
          <div class="pdp-set-picker__direction-section-head">
            <span class="pdp-set-picker__direction-label">Direction</span>
          </div>
          <textarea
            class="pdp-set-picker__direction-text"
            rows="3"
            :placeholder="directionPlaceholderFor(row)"
            :value="row.userInstructions || ''"
            :disabled="!rowEditable(rowIdx)"
            @input="setRowDirection(rowIdx, $event.target.value)"
          />
          <div class="pdp-set-picker__direction-section">
            <div class="pdp-set-picker__direction-section-head">
              <FileTextIcon
                :width="12"
                :height="12"
              />
              <span class="pdp-set-picker__direction-label">Overlay text</span>
            </div>
            <textarea
              class="pdp-set-picker__direction-text"
              rows="3"
              :placeholder="overlayTextPlaceholder"
              :value="row.overlayText || ''"
              :disabled="!rowEditable(rowIdx)"
              @input="setRowOverlayText(rowIdx, $event.target.value)"
            />
            <div class="pdp-set-picker__overlay-mode">
              <label
                class="pdp-set-picker__overlay-mode-btn"
                :class="{ 'is-active': overlayModeOf(row) === 'editable' }"
              >
                <input
                  type="radio"
                  :name="`overlay-mode-${rowIdx}`"
                  value="editable"
                  :checked="overlayModeOf(row) === 'editable'"
                  :disabled="!rowEditable(rowIdx)"
                  @change="setRowOverlayTextMode(rowIdx, 'editable')"
                >
                Editable layer
              </label>
              <label
                class="pdp-set-picker__overlay-mode-btn"
                :class="{ 'is-active': overlayModeOf(row) === 'part_of_image' }"
              >
                <input
                  type="radio"
                  :name="`overlay-mode-${rowIdx}`"
                  value="part_of_image"
                  :checked="overlayModeOf(row) === 'part_of_image'"
                  :disabled="!rowEditable(rowIdx)"
                  @change="setRowOverlayTextMode(rowIdx, 'part_of_image')"
                >
                Part of image
              </label>
            </div>
          </div>
          <div class="pdp-set-picker__direction-section">
            <div class="pdp-set-picker__ref-tabs">
              <button
                type="button"
                class="pdp-set-picker__ref-tab"
                :class="{ 'is-active': refTabFor(rowIdx) === 'product' }"
                :disabled="!rowEditable(rowIdx)"
                @click="setRefTab(rowIdx, 'product')"
              >
                Product images
                <span
                  v-if="productImagesFor(rowIdx).length"
                  class="pdp-set-picker__ref-tab-dot"
                />
              </button>
              <button
                type="button"
                class="pdp-set-picker__ref-tab"
                :class="{ 'is-active': refTabFor(rowIdx) === 'logo' }"
                :disabled="!rowEditable(rowIdx)"
                @click="setRefTab(rowIdx, 'logo')"
              >
                Brand logo
                <span
                  v-if="brandLogoByRowIdx[rowIdx]"
                  class="pdp-set-picker__ref-tab-dot"
                />
              </button>
            </div>

            <template v-if="refTabFor(rowIdx) === 'product'">
              <div
                v-for="(img, imgIdx) in productImagesFor(rowIdx)"
                :key="`${row._uid}-prod-${imgIdx}`"
                class="pdp-set-picker__logo-selected pdp-set-picker__product-item"
              >
                <img
                  :src="img.url"
                  class="pdp-set-picker__logo-thumb-sm"
                >
                <span class="pdp-set-picker__logo-name">{{ img.name || 'Product image' }}</span>
                <button
                  type="button"
                  class="pdp-set-picker__ref-remove"
                  :disabled="!rowEditable(rowIdx)"
                  :aria-label="`Remove ${img.name || 'product image'}`"
                  @click="removeProductImage(rowIdx, imgIdx)"
                >
                  <i class="bi bi-x-lg" />
                </button>
              </div>
              <div
                class="pdp-set-picker__ref-dropzone"
                :class="{ 'is-dragover': dropzoneDragActive }"
                @dragenter.prevent="dropzoneDragActive = true"
                @dragover.prevent="onDropzoneDragOver($event)"
                @dragleave="onDropzoneDragLeave($event)"
                @drop.prevent.stop="onDropImage(rowIdx, 'product', $event)"
              >
                <button
                  type="button"
                  class="pdp-set-picker__ref-link"
                  :disabled="!rowEditable(rowIdx)"
                  @click="requestProductImage(rowIdx, 'upload')"
                >
                  {{ productImagesFor(rowIdx).length ? 'Add more' : 'Upload' }}
                </button>
                <span class="pdp-set-picker__ref-sep">,</span>
                <button
                  type="button"
                  class="pdp-set-picker__ref-link"
                  :disabled="!rowEditable(rowIdx)"
                  @click="pasteFromClipboardClick(rowIdx, 'product')"
                >
                  paste
                </button>
                <span class="pdp-set-picker__ref-sep">, or</span>
                <button
                  type="button"
                  class="pdp-set-picker__ref-link"
                  :disabled="!rowEditable(rowIdx)"
                  @click="requestProductImage(rowIdx, 'browser')"
                >
                  choose from Files
                </button>
              </div>
              <label class="pdp-set-picker__ref-all">
                <input
                  type="checkbox"
                  :checked="isProductApplyAll(rowIdx)"
                  :disabled="!rowEditable(rowIdx) || !productImagesFor(rowIdx).length"
                  @change="toggleProductApplyAll(rowIdx, $event.target.checked)"
                >
                Use for all images
              </label>
            </template>

            <template v-else>
              <div
                v-if="brandLogoByRowIdx[rowIdx]"
                class="pdp-set-picker__logo-selected"
              >
                <img
                  :src="brandLogoByRowIdx[rowIdx].url"
                  class="pdp-set-picker__logo-thumb-sm"
                >
                <span class="pdp-set-picker__logo-name">{{ brandLogoByRowIdx[rowIdx].name || 'Logo' }}</span>
                <button
                  type="button"
                  class="pdp-set-picker__logo-change"
                  :disabled="!rowEditable(rowIdx)"
                  @click="requestBrandLogo(rowIdx)"
                >
                  Change
                </button>
                <button
                  type="button"
                  class="pdp-set-picker__ref-remove"
                  :disabled="!rowEditable(rowIdx)"
                  aria-label="Remove brand logo"
                  @click="removeBrandLogo(rowIdx)"
                >
                  <i class="bi bi-x-lg" />
                </button>
              </div>
              <div
                v-else
                class="pdp-set-picker__ref-dropzone"
                :class="{ 'is-dragover': dropzoneDragActive }"
                @dragenter.prevent="dropzoneDragActive = true"
                @dragover.prevent="onDropzoneDragOver($event)"
                @dragleave="onDropzoneDragLeave($event)"
                @drop.prevent.stop="onDropImage(rowIdx, 'logo', $event)"
              >
                <button
                  type="button"
                  class="pdp-set-picker__ref-link"
                  :disabled="!rowEditable(rowIdx)"
                  @click="requestBrandLogo(rowIdx, 'upload')"
                >
                  Upload
                </button>
                <span class="pdp-set-picker__ref-sep">,</span>
                <button
                  type="button"
                  class="pdp-set-picker__ref-link"
                  :disabled="!rowEditable(rowIdx)"
                  @click="pasteFromClipboardClick(rowIdx, 'logo')"
                >
                  paste
                </button>
                <span class="pdp-set-picker__ref-sep">, or</span>
                <button
                  type="button"
                  class="pdp-set-picker__ref-link"
                  :disabled="!rowEditable(rowIdx)"
                  @click="requestBrandLogo(rowIdx, 'browser')"
                >
                  choose from Files
                </button>
              </div>
            </template>
          </div>
          <div class="separator w-100" />
          <div class="pdp-set-picker__direction-foot">
            <span class="pdp-set-picker__direction-note">Saved automatically · applies only to this image.</span>
            <button
              type="button"
              class="pdp-set-picker__direction-done"
              @click="directionOpenIdx = null"
            >
              Done
            </button>
          </div>
        </div>

        <div class="pdp-set-picker__card-frame">
          <div class="pdp-set-picker__card-body">
            <img
              v-if="cardPreview(row)"
              :src="cardPreview(row)"
              :alt="row.label"
              class="pdp-set-picker__card-img"
            >
            <!-- Preset to Upload but still empty: prompt for the image instead of
                 showing the AI sparkle, which would read as "will generate". (POR-14020) -->
            <button
              v-else-if="needsOwnImage(row)"
              type="button"
              class="pdp-set-picker__card-upload"
              :disabled="!rowEditable(rowIdx)"
              @click.stop="toggleYoursMenu(rowIdx)"
            >
              <i class="bi bi-upload" />
              <span>Upload your image</span>
            </button>
            <i
              v-else
              class="bi bi-stars pdp-set-picker__card-spark"
            />
          </div>
          <span
            v-if="!!row.reference_image_url"
            class="pdp-set-picker__card-ref-badge"
          >
            <i class="bi bi-stars" /> AI
          </span>
          <span
            v-if="rowGenerated(rowIdx)"
            class="pdp-set-picker__card-done"
            title="Already generated"
          >
            <i class="bi bi-check-lg" />
          </span>
          <button
            v-if="cardPreview(row)"
            type="button"
            class="pdp-set-picker__card-expand"
            aria-label="Preview image"
            @click.stop="openImagePreview(cardPreview(row), row.label)"
          >
            <i class="bi bi-arrows-angle-expand" />
          </button>
          <div class="pdp-set-picker__card-label">
            {{ row.label }}
          </div>
        </div>
      </div>
    </div>

    <!--
      Image size lives IN the picker instead of as a follow-up chat question, so
      it is visible without scrolling to the bottom of the conversation. Only the
      AI/scene cards are rendered at this size, so the row is hidden when nothing
      will be generated — matching the old flow, which skipped the question in
      that case. (POR-14023)
    -->
    <div
      v-if="showSizeRow"
      class="pdp-set-picker__size"
    >
      <span class="pdp-set-picker__size-label">Image size</span>
      <div class="pdp-set-picker__size-options">
        <button
          v-for="opt in sizeOptions"
          :key="opt"
          type="button"
          class="pdp-set-picker__size-btn"
          :class="{ 'is-active': opt === selectedSize }"
          :disabled="disabled"
          @click="selectSize(opt)"
        >
          {{ opt.replace('x', ' x ') }}
        </button>
      </div>
      <span
        v-if="needsSize && !disabled"
        class="pdp-set-picker__size-hint"
      >Pick one to continue</span>
    </div>

    <div
      ref="footer"
      class="pdp-set-picker__footer"
    >
      <div
        class="pdp-set-picker__create-split"
        :class="{ 'is-open': createMenuOpen }"
      >
        <button
          type="button"
          class="btn btn-primary pdp-set-picker__create"
          :disabled="!canCreate"
          @click="confirm('parallel')"
        >
          <i class="bi bi-lightning-charge-fill" />
          Create {{ localRows.length }} at once
        </button>
        <button
          type="button"
          class="pdp-set-picker__create-caret"
          :disabled="!canCreate"
          aria-label="More create options"
          @click="toggleCreateMenu"
        >
          <i class="bi bi-chevron-down" />
        </button>
        <div
          v-if="createMenuOpen"
          class="pdp-set-picker__create-menu"
        >
          <button
            type="button"
            class="pdp-set-picker__create-menu-item"
            :disabled="!canCreate"
            @click="chooseCreate('parallel')"
          >
            <i class="bi bi-lightning-charge-fill" />
            Create {{ localRows.length }} at once
          </button>
          <button
            type="button"
            class="pdp-set-picker__create-menu-item"
            :disabled="!canCreate"
            @click="chooseCreate('sequential')"
          >
            <i class="bi bi-arrow-right" />
            One at a time
          </button>
        </div>
      </div>
      <span
        v-if="rowsNeedingOwnImage && !disabled"
        class="pdp-set-picker__upload-hint"
      >
        {{ rowsNeedingOwnImage }}
        {{ rowsNeedingOwnImage === 1 ? 'card needs' : 'cards need' }} your image
      </span>
      <button
        type="button"
        class="pdp-set-picker__browse"
        :disabled="disabled"
        @click="$emit('open-gallery')"
      >
        Browse full gallery →
      </button>
    </div>

    <AIChatWidgetPdpTemplateLightbox
      :visible="!!lightbox.visible"
      :template="lightbox.template || {}"
      :is-selected="lightbox.isSelected"
      @select-template="onLightboxSelect"
      @deselect-template="onLightboxDeselect"
      @close="closeLightbox"
    />

    <div
      v-if="imagePreview.visible"
      class="pdp-set-picker__preview-lightbox"
      @click.self="closeImagePreviewModal"
    >
      <button
        type="button"
        class="pdp-set-picker__preview-close"
        aria-label="Close"
        @click="closeImagePreviewModal"
      >
        <i class="bi bi-x-lg" />
      </button>
      <img
        :src="imagePreview.url"
        :alt="imagePreview.label"
        class="pdp-set-picker__preview-img"
      >
    </div>
  </div>
</template>

<script>
import Sortable from 'sortablejs';
import { BUILT_IN_TYPES, PDP_IMAGE_SIZES, normalizePdpSize, reindexRowSelections } from '@frontend/store/creative-studio/chat-function-handlers/pdp-generation-paths';
import { buildRowCustomizationPatch, brandLogoFromRow } from '@frontend/store/creative-studio/pdp-picker-edit';
import { remapIndexKeys, shiftScalarIndex, nextDuplicateLabel } from '@frontend/store/creative-studio/pdp-row-maps';
import AIChatWidgetPdpTemplateLightbox from './AIChatWidgetPdpTemplateLightbox.vue';
import {
  FileText as FileTextIcon
} from "lucide-vue";

let rowUidSeq = 0;

// Overlay text is baked into the generated image by default. "Editable layer" keeps it
// as a separate, editable text layer, which is the exception rather than the norm.
// Rows persisted before this default flipped keep whatever they were saved with.
// (POR-13881-3)
const DEFAULT_OVERLAY_TEXT_MODE = 'part_of_image';

function nextRowUid() {
  rowUidSeq += 1;
  return `pdp-row-${rowUidSeq}`;
}

export default {
  name: 'AIChatWidgetPdpImageSetPicker',
  components: {
    AIChatWidgetPdpTemplateLightbox,
    FileTextIcon
  },
  props: {
    rows: { type: Array, required: true },
    templatesBySubtype: { type: Object, default: () => ({}) },
    presetSources: { type: Array, default: () => [] },
    initialPresetId: { type: String, default: '' },
    // Output size for the AI cards, as "1500x1500". Pre-filled when a brief
    // already specified one, and written back on confirm so a remount (reload,
    // fetchMessages) re-renders the picked size. (POR-14023)
    initialSize: { type: String, default: '' },
    productLabel: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    // Which row the sequential ("1 image at a time") queue is on. Rows AFTER it
    // have not been generated yet and stay editable via the wand even though the
    // widget as a whole is disabled. null / undefined = not a sequential run
    // (parallel grid, or the picker before confirm). (POR-13869)
    sequentialCursor: { type: Number, default: null },
    referenceMode: { type: Boolean, default: false },
    productImageUrl: { type: String, default: '' },
    ownImageResult: { type: Object, default: null },
    brandLogoResult: { type: Object, default: null },
    productImageResult: { type: Object, default: null },
    // Set only when this session was staged from a Creative Brief — either by
    // opening Creative Studio with ?brief=, or by picking a brief in the chat.
    // Both make the toolbar offer the brief. (POR-13996)
    briefId: { type: [String, Number], default: null },
    briefTitle: { type: String, default: '' },
  },
  data() {
    return {
      localRows: this.rows.map((r) => ({ overlayTextMode: DEFAULT_OVERLAY_TEXT_MODE, ...r, _uid: r._uid || nextRowUid() })),
      initialRowCount: this.rows.length,
      activePresetId: this.initialPresetId || (this.presetSources[0] ? this.presetSources[0].id : ''),
      selectedSize: normalizePdpSize(this.initialSize),
      presetOpen: false,
      addableTypesOpen: false,
      createMenuOpen: false,
      lightbox: { visible: false, rowIdx: null, template: null, isSelected: false },
      imagePreview: { visible: false, url: '', label: '' },
      directionOpenIdx: null,
      directionAbove: {},
      directionLeftAlign: {},
      pendingLogoDirectionIdx: null,
      pendingLogoRowIdx: null,
      brandLogoByRowIdx: {},
      // Per-tile Product images (POR-13766): the packshots the AI preserves as the
      // base for THIS tile. Keyed by array index (mirrors brandLogoByRowIdx). The
      // value is an ARRAY — types like cross-sell compose several products, so a
      // multi-select keeps every pick instead of just the first. (POR-13766-4)
      pendingProductDirectionIdx: null,
      pendingProductRowIdx: null,
      productImageByRowIdx: {},
      // Which reference sub-tab (product | logo) is showing per card. Product default.
      activeRefTab: {},
      // "Use for all images": one tile's product images become the base for every
      // generative tile that has no per-tile product image of its own. (array)
      productImageApplyAll: false,
      productImageApplyAllRowIdx: null,
      productImageApplyAllValue: null,
      // Drag-over highlight for the Product image / Brand logo dropzone. (POR-13766)
      dropzoneDragActive: false,
      yoursMenuIdx: null,
      galleryOpenIdx: null,
      // 3-dot "More actions" menu per card: which card's menu is open, and whether
      // it has to open upward because the card sits near the viewport bottom. (POR-13854)
      cardMenuIdx: null,
      cardMenuAbove: {},
      referenceInput: '',
      sortable: null,
      // Debounce handle for post-confirm row edits (POR-13869). Not reactive state
      // the template reads — declared here for consistency with the component's
      // other non-reactive instance handles (e.g. `sortable` above).
      rowUpdateTimer: null,
      rowUpdatePendingIdx: null,
      directionPlaceholder: 'e.g. "warm afternoon kitchen, soft shadows" or "minimal white background, dramatic angle"',
      overlayTextPlaceholder: 'e.g. "Now 20% off"',
      generationMode: (typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem('cs_pdp_generation_mode')) || 'parallel',
    };
  },
  computed: {
    activePresetLabel() {
      const src = this.presetSources.find((s) => s.id === this.activePresetId);
      return src ? src.label : '';
    },
    // Older conversations persisted the brief id without its title; the button
    // still works, it just reads "From brief: Brief".
    briefLabel() {
      return String(this.briefTitle || '').trim() || 'Brief';
    },
    anyTemplatesAvailable() {
      return Object.values(this.templatesBySubtype || {}).some((arr) => arr && arr.length);
    },
    addableTypes() {
      const present = new Set(this.localRows.map((r) => r.type));
      return Object.values(BUILT_IN_TYPES)
        .filter((t) => !present.has(t.key))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    // A brief can specify a size outside the four standard ones. Offer it as an
    // extra option rather than dropping it — otherwise a brief that already
    // answered the size question would be forced to answer it again, with a
    // different answer. (POR-14023)
    sizeOptions() {
      const seeded = normalizePdpSize(this.initialSize);
      return seeded && !PDP_IMAGE_SIZES.includes(seeded)
        ? [...PDP_IMAGE_SIZES, seeded]
        : PDP_IMAGE_SIZES;
    },
    // Only AI/scene cards are rendered from scratch — a set of gallery templates
    // and uploads ignores the size entirely, which is why the old flow never
    // asked for one in that case. (POR-14023)
    showSizeRow() {
      return this.localRows.some((r) => r.mode === 'ai' || r.mode === 'scene');
    },
    needsSize() {
      return this.showSizeRow && !this.selectedSize;
    },
    // Cards set to "Yours" with nothing picked yet. Generating would emit an
    // attachment-less result (keepUseAsIsRow), so Create waits for them. (POR-14020)
    rowsNeedingOwnImage() {
      return this.localRows.filter((r) => this.needsOwnImage(r)).length;
    },
    canCreate() {
      return !this.disabled && !!this.localRows.length && !this.needsSize && !this.rowsNeedingOwnImage;
    },
  },
  watch: {
    localRows() {
      if (this.addableTypes.length === 0) {
        this.addableTypesOpen = false;
      }
    },
    disabled(val) {
      if (this.sortable) {
        this.sortable.option('disabled', val);
      }
    },
    // Every close path for the Customize popover — the Done button, toggling the
    // wand off, an outside click, and Escape — funnels through directionOpenIdx,
    // so one watcher flushes them all. Gated on rowUpdateTimer so opening and
    // closing the popover without changing anything does not cost a wasted
    // message-save round-trip. (POR-13869)
    directionOpenIdx(next, prev) {
      if (prev !== null && this.rowUpdateTimer) {
        this.flushRowUpdate(prev);
      }
    },
    brandLogoResult(res) {
      if (!res || !res.url) { return; }

      const idx = this.pendingLogoRowIdx !== null
        ? this.pendingLogoRowIdx
        : this.localRows.findIndex((r) => r.rowIndex === res.rowIndex);

      if (idx === -1 || idx === null) { return; }

      this.$set(this.brandLogoByRowIdx, idx, { url: res.url, name: res.name || '' });

      this.pendingLogoRowIdx = null;

      if (this.pendingLogoDirectionIdx !== null) {
        this.directionOpenIdx = this.pendingLogoDirectionIdx;
        this.pendingLogoDirectionIdx = null;
      }

      this.queueRowUpdate(idx);
    },
    // The parent resolved a Product image (upload / File Browser / Salsify) and
    // wrote it back. Store it as the per-tile base packshot the AI preserves.
    // (POR-13766)
    productImageResult(res) {
      if (!res) { return; }

      // The parent sends `images: [{url,name}]` (a File Browser pick can be several);
      // the legacy single `{url,name}` shape is still accepted. (POR-13766-4)
      const incoming = (Array.isArray(res.images) && res.images.length)
        ? res.images.filter((im) => im && im.url).map((im) => ({ url: im.url, name: im.name || '' }))
        : (res.url ? [{ url: res.url, name: res.name || '' }] : []);

      if (!incoming.length) { return; }

      const idx = this.pendingProductRowIdx !== null
        ? this.pendingProductRowIdx
        : this.localRows.findIndex((r) => r.rowIndex === res.rowIndex);

      if (idx === -1 || idx === null) { return; }

      this.appendProductImages(idx, incoming);

      this.pendingProductRowIdx = null;

      if (this.pendingProductDirectionIdx !== null) {
        this.directionOpenIdx = this.pendingProductDirectionIdx;
        this.pendingProductDirectionIdx = null;
      }

      this.queueRowUpdate(idx);
    },
    // The parent resolved a user-supplied image (upload / File Browser / Salsify)
    // and wrote it back. Apply it to the target row as a use-as-is "yours" image.
    // `nonce` on the result guarantees the watcher fires on repeat picks. (POR-13271)
    ownImageResult(res) {
      if (!res || !res.url) { return; }

      const idx = this.localRows.findIndex((r) => r.rowIndex === res.rowIndex);

      if (idx === -1) { return; }

      this.$set(this.localRows, idx, {
        ...this.localRows[idx],
        mode: 'yours',
        own_image_url: res.url,
        own_image_thumb: res.thumb || res.url,
        own_image_source: res.source || null,
        template_id: null,
        template_thumb: null,
        template_image_url: null,
        scene: null,
      });
    },
  },
  created() {
    // Lock hero rows to the "Original" (use-as-is) mode on init. Idempotent, so
    // it's safe across remounts (reload) where rows already carry mode:'original'.
    this.localRows = this.localRows.map((r) => this.withHeroMode(r));
    // A row can arrive with a Product image already chosen — e.g. a brief that
    // pasted a product image under an image type's description (POR-13810), or a
    // remount after the user picked one. Seed it into the picker state so it shows
    // as the base in the Customize popover (filled dropzone + green dot) and stays
    // editable/removable like a manual pick.
    this.localRows.forEach((r, i) => {
      if (!r) { return; }
      const names = Array.isArray(r.productImageNames) ? r.productImageNames : [];
      const seeded = (Array.isArray(r.productImageUrls) && r.productImageUrls.length)
        ? r.productImageUrls
          .filter(Boolean)
          .map((url, n) => ({ url, name: names[n] || 'Product image' }))
        : (r.productImageUrl
          ? [{ url: r.productImageUrl, name: r.productImageName || 'Product image' }]
          : []);
      if (seeded.length) {
        this.$set(this.productImageByRowIdx, i, seeded);
      }
      // Same for a persisted brand logo: without this the Customize popover looks
      // empty after a reload even though the row carries one. (POR-13869)
      const logo = brandLogoFromRow(r);
      if (logo) {
        this.$set(this.brandLogoByRowIdx, i, logo);
      }
    });
  },
  mounted() {
    document.addEventListener('mousedown', this.onDocumentMousedown);
    document.addEventListener('keydown', this.onDocumentKeydown);
    // Capture phase: intercept image pastes before the CS chat composer. (POR-13766)
    document.addEventListener('paste', this.onDocumentPaste, true);

    this.$nextTick(() => this.initSortable());
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onDocumentMousedown);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    document.removeEventListener('paste', this.onDocumentPaste, true);

    if (this.rowUpdateTimer) {
      this.flushRowUpdate(this.rowUpdatePendingIdx);
    }

    if (this.sortable) {
      this.sortable.destroy();
    }
  },
  methods: {
    templatesForRow(row) {
      if (this.isHeroRow(row)) return [];
      return (this.templatesBySubtype && this.templatesBySubtype[row.type]) || [];
    },
    hasTemplates(row) {
      return this.templatesForRow(row).length > 0;
    },
    // A card the user owes an image: "Yours" mode with nothing picked. Reference
    // rows already carry their image, so they never qualify. (POR-14020)
    needsOwnImage(row) {
      return !!row
        && row.mode === 'yours'
        && !row.own_image_url
        && !row.own_image_thumb
        && !row.reference_image_url;
    },
    // Which image the card thumbnail shows for the row's current mode. Falls back
    // to the sparkle placeholder (returns '') when the mode has no fixed image
    // (AI/Scene generate at run time). (POR-13766)
    cardPreview(row) {
      if (!row) return '';
      if (row.reference_image_url) return row.reference_image_url;
      if (row.mode === 'yours' && (row.own_image_thumb || row.own_image_url)) {
        return row.own_image_thumb || row.own_image_url;
      }
      if (row.mode === 'gallery' && (row.template_thumb || row.template_image_url)) {
        return row.template_thumb || row.template_image_url;
      }
      if (this.isHeroRow(row) && row.mode === 'original') {
        return this.heroImageFor(row);
      }
      return '';
    },
    // Identifies a hero row. It defaults to "Original" (use-as-is) but can be
    // switched to AI or Yours on hover; this flag only controls the Original tile
    // + the no-gallery/scene/wand treatment, not the selected mode. (POR-13271)
    isHeroRow(row) {
      return !!row && row.type === 'hero';
    },
    heroImageFor(row) {
      return (row && row.original_image_url) || this.productImageUrl || '';
    },
    withHeroMode(row) {
      if (this.isHeroRow(row)) {
        // A sequence slot preset to Upload beats the Hero default — the user
        // already said they supply this one themselves. (POR-14020)
        if (row.mode === 'yours') return row;
        const heroImage = row.original_image_url || this.productImageUrl || null;
        // Hero defaults to the product packshot shown "Original" (use-as-is).
        // But with no packshot available (e.g. a brief opened without a product),
        // locking Hero to 'original' leaves a permanently-blank tile that never
        // generates — so fall back to AI generation like the other rows. The
        // user can still switch to Original/Yours once a product image exists.
        if (heroImage) {
          return { ...row, mode: 'original', original_image_url: heroImage };
        }
        return { ...row, mode: 'ai', original_image_url: null };
      }
      return row;
    },
    // Per-image direction (the wand) only applies to rows that actually run a
    // prompt — AI and Scene. Not Hero-original, gallery, or "Yours". (POR-13271)
    isGenerativeRow(row) {
      return !!row && (row.mode === 'ai' || row.mode === 'scene') && !this.isHeroRow(row);
    },
    // A row is still editable when the picker is live, or — once confirmed in the
    // sequential flow — when the queue has not reached it yet. The cursor row is
    // in flight (or awaiting "Keep & next") and has its own "Refine with feedback",
    // so it is NOT editable here. (POR-13869)
    rowEditable(rowIdx) {
      if (!this.disabled) return true;
      return this.sequentialCursor !== null && rowIdx > this.sequentialCursor;
    },
    // Rows the sequential queue has already settled. Marked with a check badge.
    rowGenerated(rowIdx) {
      return this.sequentialCursor !== null && rowIdx < this.sequentialCursor;
    },
    // Post-confirm edits (POR-13869): the set is already running, so a change has
    // to reach the live queue. Debounced — every keystroke in the Direction /
    // Overlay text areas would otherwise cost a message save round-trip. No-op
    // before confirm, where confirm() still carries everything.
    queueRowUpdate(rowIdx) {
      if (!this.disabled || !this.rowEditable(rowIdx)) { return; }

      if (this.rowUpdateTimer && this.rowUpdatePendingIdx !== rowIdx) {
        this.flushRowUpdate(this.rowUpdatePendingIdx);
      }

      this.rowUpdatePendingIdx = rowIdx;
      clearTimeout(this.rowUpdateTimer);
      this.rowUpdateTimer = setTimeout(() => this.flushRowUpdate(rowIdx), 400);
    },
    // Compute and emit the current patch for one row right now, with no timer
    // bookkeeping. Shared by flushRowUpdate() (the row owning the debounce) and
    // flushInheritingRows() (OTHER rows that never had a debounce armed for
    // them), so the latter can never clobber a debounce that belongs to a
    // different row. (POR-13869)
    emitRowPatch(rowIdx) {
      if (rowIdx === null || rowIdx === undefined) { return; }
      if (!this.disabled || !this.rowEditable(rowIdx)) { return; }

      const row = this.localRows[rowIdx];
      if (!row) { return; }

      // Same per-tile / "use for all" precedence confirm() applies. (POR-13766-4)
      const own = this.productImagesFor(rowIdx);
      const applyAll = (this.productImageApplyAll && Array.isArray(this.productImageApplyAllValue))
        ? this.productImageApplyAllValue
        : [];

      this.$emit('row-update', {
        rowIndex: rowIdx,
        patch: buildRowCustomizationPatch({
          row,
          brandLogo: this.brandLogoByRowIdx[rowIdx] || null,
          productImages: own.length ? own : applyAll,
        }),
      });
    },
    flushRowUpdate(rowIdx) {
      clearTimeout(this.rowUpdateTimer);
      this.rowUpdateTimer = null;
      this.rowUpdatePendingIdx = null;

      this.emitRowPatch(rowIdx);
    },
    // "Use for all images" is set-wide (POR-13869): changing or clearing the
    // default silently changes what every OTHER pending row with no per-tile
    // product image of its own will generate with. Those rows never had a
    // debounce armed (only the source row's popover was open), so emit for them
    // immediately instead of queueing — this is a single discrete click, not
    // typing. The source row itself stays on its own queueRowUpdate call so it
    // is not emitted twice.
    flushInheritingRows(sourceRowIdx) {
      this.localRows.forEach((row, idx) => {
        if (idx === sourceRowIdx) { return; }
        if (!this.rowEditable(idx)) { return; }
        if (this.productImagesFor(idx).length) { return; }
        this.emitRowPatch(idx);
      });
    },
    hasDirection(row, rowIdx) {
      return !!(row && (
        (row.userInstructions && row.userInstructions.trim()) ||
        (row.overlayText && row.overlayText.trim()) ||
        (rowIdx !== undefined ? this.brandLogoByRowIdx[rowIdx] : null) ||
        (rowIdx !== undefined ? this.productImagesFor(rowIdx).length : 0)
      ));
    },
    // Per-tile product images, always an array (empty when none picked). (POR-13766-4)
    productImagesFor(rowIdx) {
      const list = this.productImageByRowIdx[rowIdx];
      return Array.isArray(list) ? list : [];
    },
    // Append picks to a tile, skipping URLs already in the list so re-picking the
    // same packshot does not duplicate a row. (POR-13766-4)
    appendProductImages(rowIdx, images) {
      const current = this.productImagesFor(rowIdx);
      const seen = new Set(current.map((im) => im.url));
      const added = (images || []).filter((im) => im && im.url && !seen.has(im.url));

      if (!added.length) { return; }

      const next = [...current, ...added];
      this.$set(this.productImageByRowIdx, rowIdx, next);

      // Keep the "use for all" snapshot in sync when the source tile changes.
      if (this.productImageApplyAll && this.productImageApplyAllRowIdx === rowIdx) {
        this.productImageApplyAllValue = next.map((im) => ({ ...im }));
        // The set-wide default just grew — every other pending row relying on it
        // needs to know. (POR-13869)
        this.flushInheritingRows(rowIdx);
      }

      this.queueRowUpdate(rowIdx);
    },
    // Per-type guidance for the direction wand. The new gallery / cross-sell /
    // iconography types depend on user-supplied specifics (background, overlay
    // text, benefit-icon list), so each shows a tailored example; everything
    // else keeps the generic default. (POR-13366)
    directionPlaceholderFor(row) {
      const byType = {
        product_gallery: 'Describe the background, tagline/logo and overlay text (e.g. neutral linen backdrop; brand logo + tagline; overlay "Same pure formula, fresh new look")',
        cross_sell: 'Add overlay text / call-to-action and any styling notes (e.g. "Find your ritual")',
        category_gallery: 'Describe the lifestyle scene + overlay text (e.g. serene bathroom with the collection displayed; overlay "Elevate your self-care")',
        feature_iconography_around: 'List the benefit icons to integrate (e.g. Longest Lasting, Sensory Experience, Sustainable, Vegan, Nourishing, Luxury)',
        feature_iconography_bottom: 'List the benefit icons to integrate (e.g. Longest Lasting, Sustainable, Vegan, Nourishing)',
      };
      return (row && byType[row.type]) || this.directionPlaceholder;
    },
    submitReference() {
      const value = (this.referenceInput || '').trim();

      if (!value || this.disabled) {
        return;
      }

      // The parent (NetworksPageV2) routes this into the existing reference flow
      // (equivalent to "based on <value>") and rebuilds the picker in reference
      // mode. (POR-13766)
      this.$emit('reference-submit', { value });
      this.referenceInput = '';
    },
    toggleDirection(rowIdx, event) {
      if (!this.rowEditable(rowIdx)) return;
      this.yoursMenuIdx = null;
      this.galleryOpenIdx = null;
      this.cardMenuIdx = null;
      this.directionOpenIdx = this.directionOpenIdx === rowIdx ? null : rowIdx;
      if (this.directionOpenIdx === rowIdx && event) {
        const rect = event.currentTarget.getBoundingClientRect();
        this.$set(this.directionAbove, rowIdx, window.innerHeight - rect.bottom < 420);
        // The 385px popover is right-anchored (opens leftward); for left-column
        // cards that pushes it under the fixed left nav. Flip to left-anchored
        // (opens rightward) when it would overflow the widget's left edge. (POR-13766-2)
        const POPOVER_WIDTH = 385;
        const widgetLeft = this.$el ? this.$el.getBoundingClientRect().left : 0;
        this.$set(this.directionLeftAlign, rowIdx, (rect.right - POPOVER_WIDTH) < widgetLeft + 8);
      }
    },
    setRowDirection(rowIdx, value) {
      if (!this.rowEditable(rowIdx)) { return; }
      this.$set(this.localRows, rowIdx, { ...this.localRows[rowIdx], userInstructions: value });
      this.queueRowUpdate(rowIdx);
    },
    clearRowDirection(rowIdx) {
      if (!this.rowEditable(rowIdx)) { return; }
      this.$set(this.localRows, rowIdx, { ...this.localRows[rowIdx], userInstructions: '' });
      this.queueRowUpdate(rowIdx);
    },
    setRowOverlayText(rowIdx, value) {
      if (!this.rowEditable(rowIdx)) { return; }
      this.$set(this.localRows, rowIdx, { ...this.localRows[rowIdx], overlayText: value });
      this.queueRowUpdate(rowIdx);
    },
    setRowOverlayTextMode(rowIdx, mode) {
      if (!this.rowEditable(rowIdx)) { return; }
      this.$set(this.localRows, rowIdx, { ...this.localRows[rowIdx], overlayTextMode: mode });
      this.queueRowUpdate(rowIdx);
    },
    // Which of the two chips is lit. Every row built here carries an explicit mode, so
    // this fallback only covers rows persisted before the default flipped — and it has
    // to stay 'editable', because that is still what the generation paths assume for a
    // row with no mode. Showing "Part of image" there would light a chip that does not
    // match what actually gets generated. (POR-13881-3)
    overlayModeOf(row) {
      return (row && row.overlayTextMode) || 'editable';
    },
    requestBrandLogo(rowIdx, source = 'browser') {
      if (!this.rowEditable(rowIdx)) { return; }

      this.pendingLogoDirectionIdx = this.directionOpenIdx;
      this.pendingLogoRowIdx = rowIdx;

      this.$emit('brand-logo-request', { rowIndex: this.localRows[rowIdx].rowIndex, source });
    },
    removeBrandLogo(rowIdx) {
      if (!this.rowEditable(rowIdx)) { return; }
      this.$set(this.brandLogoByRowIdx, rowIdx, null);
      // Also clear the row's own fields, not just the popover slot — otherwise a
      // logo hydrated from a persisted row (POR-13869 created() seeding) survives
      // an unconfirmed removal because confirm() only WRITES brandLogoUrl when the
      // slot is truthy, never clears it.
      this.$set(this.localRows, rowIdx, { ...this.localRows[rowIdx], brandLogoUrl: null, brandLogoName: '' });
      this.queueRowUpdate(rowIdx);
    },
    refTabFor(rowIdx) {
      return this.activeRefTab[rowIdx] || 'product';
    },
    setRefTab(rowIdx, tab) {
      if (!this.rowEditable(rowIdx)) { return; }
      this.$set(this.activeRefTab, rowIdx, tab);
    },
    requestProductImage(rowIdx, source = 'browser') {
      if (!this.rowEditable(rowIdx)) { return; }

      this.pendingProductDirectionIdx = this.directionOpenIdx;
      this.pendingProductRowIdx = rowIdx;

      this.$emit('product-image-request', { rowIndex: this.localRows[rowIdx].rowIndex, source });
    },
    // Apply an image File (from a Ctrl+V paste or the async clipboard) to the
    // row's active reference slot. Read as a base64 data URI so it renders with no
    // upload round-trip — the same path pickOwnImageFromComputer uses; the URI is
    // durabilized to S3 on save. (POR-13766)
    readImageFileAsPayload(file) {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const uri = reader.result;
          resolve((typeof uri === 'string' && uri.startsWith('data:'))
            ? { url: uri, name: file.name || 'Pasted image' }
            : null);
        };
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(file);
      });
    },
    // Apply one or more image Files to the row's active slot. The product slot takes
    // ALL of them (cross-sell composites several packshots); the logo slot is a
    // single image, so it keeps the first. (POR-13766-4)
    async applyImageFilesToSlot(rowIdx, tab, files) {
      if (!this.rowEditable(rowIdx)) { return; }

      const images = (files || []).filter((f) => f && String(f.type || '').startsWith('image/'));
      if (!images.length) { return; }

      if (tab === 'logo') {
        const payload = await this.readImageFileAsPayload(images[0]);
        if (payload) {
          this.$set(this.brandLogoByRowIdx, rowIdx, payload);
          this.queueRowUpdate(rowIdx);
        }
        return;
      }

      const payloads = (await Promise.all(images.map((f) => this.readImageFileAsPayload(f)))).filter(Boolean);
      if (payloads.length) { this.appendProductImages(rowIdx, payloads); }
    },
    applyPastedImageToSlot(rowIdx, tab, file) {
      this.applyImageFilesToSlot(rowIdx, tab, [file]);
    },
    imageFileFromClipboard(items) {
      const list = Array.from(items || []);
      const imgItem = list.find((it) => it && it.type && it.type.indexOf('image') === 0);
      return imgItem && imgItem.getAsFile ? imgItem.getAsFile() : null;
    },
    // While a Customize popover is open, a pasted IMAGE lands in the active slot
    // instead of the Creative Studio chat composer. Runs in the capture phase and
    // stops propagation so the composer's own paste handler never sees it. Text
    // pastes (no image) are ignored, so the Direction/Overlay textareas still work.
    // (POR-13766)
    onDocumentPaste(event) {
      if (this.directionOpenIdx === null || !this.rowEditable(this.directionOpenIdx)) { return; }

      const cd = event.clipboardData || window.clipboardData;
      if (!cd) { return; }

      const file = this.imageFileFromClipboard(cd.items);
      if (!file) { return; }

      event.preventDefault();
      event.stopPropagation();

      const rowIdx = this.directionOpenIdx;
      this.applyPastedImageToSlot(rowIdx, this.refTabFor(rowIdx), file);
    },
    // Clicking the "paste" link reads the clipboard via the async API (secure
    // contexts / https). On http or when denied it no-ops — the Ctrl+V capture
    // above still handles the paste. (POR-13766)
    async pasteFromClipboardClick(rowIdx, tab) {
      if (!this.rowEditable(rowIdx)) { return; }

      try {
        if (!navigator.clipboard || !navigator.clipboard.read) { return; }
        const items = await navigator.clipboard.read();
        for (const item of items) {
          const type = (item.types || []).find((t) => t.indexOf('image') === 0);
          if (type) {
            const blob = await item.getType(type);
            this.applyPastedImageToSlot(rowIdx, tab, new File([blob], 'Pasted image', { type }));
            return;
          }
        }
      } catch (e) {
        // Non-secure context or no clipboard permission — Ctrl+V still works.
      }
    },
    onDropzoneDragOver(event) {
      if (event.dataTransfer) { event.dataTransfer.dropEffect = 'copy'; }
      this.dropzoneDragActive = true;
    },
    onDropzoneDragLeave(event) {
      // Ignore leaves onto the dropzone's own links/spans; only clear on real exit.
      if (!event.currentTarget || !event.currentTarget.contains(event.relatedTarget)) {
        this.dropzoneDragActive = false;
      }
    },
    // A file dragged onto the dropzone lands in the active slot instead of the CS
    // chat surface — NetworksPageV2's isDragOverDock defers this zone to us. (POR-13766)
    onDropImage(rowIdx, tab, event) {
      this.dropzoneDragActive = false;
      if (!this.rowEditable(rowIdx)) { return; }

      // Drop MANY at once — every dropped image lands in the product slot. (POR-13766-4)
      const files = Array.from((event.dataTransfer && event.dataTransfer.files) || []);
      this.applyImageFilesToSlot(rowIdx, tab, files);
    },
    removeProductImage(rowIdx, imgIdx) {
      if (!this.rowEditable(rowIdx)) { return; }

      const next = this.productImagesFor(rowIdx).filter((im, i) => i !== imgIdx);
      this.$set(this.productImageByRowIdx, rowIdx, next);

      if (this.productImageApplyAllRowIdx === rowIdx) {
        // Emptying the "use for all" source tile clears the set-wide default; otherwise
        // just keep the snapshot in step with what is left. (POR-13766-4)
        if (!next.length) {
          this.productImageApplyAll = false;
          this.productImageApplyAllRowIdx = null;
          this.productImageApplyAllValue = null;
        } else if (this.productImageApplyAll) {
          this.productImageApplyAllValue = next.map((im) => ({ ...im }));
        }
        // The set-wide default just changed or cleared — every other pending row
        // relying on it needs to know. (POR-13869)
        this.flushInheritingRows(rowIdx);
      }

      this.queueRowUpdate(rowIdx);
    },
    isProductApplyAll(rowIdx) {
      return this.productImageApplyAll && this.productImageApplyAllRowIdx === rowIdx;
    },
    // "Use for all images": snapshot this tile's product image as the set-wide
    // default. Radio-like — checking it on another tile moves the default. (POR-13766)
    toggleProductApplyAll(rowIdx, checked) {
      if (!this.rowEditable(rowIdx)) { return; }
      if (checked) {
        const imgs = this.productImagesFor(rowIdx);
        if (!imgs.length) { return; }
        this.productImageApplyAll = true;
        this.productImageApplyAllRowIdx = rowIdx;
        this.productImageApplyAllValue = imgs.map((im) => ({ url: im.url, name: im.name || '' }));
      } else {
        this.productImageApplyAll = false;
        this.productImageApplyAllRowIdx = null;
        this.productImageApplyAllValue = null;
      }
      // The set-wide default just changed or cleared — every other pending row
      // relying on it needs to know. (POR-13869)
      this.flushInheritingRows(rowIdx);
      this.queueRowUpdate(rowIdx);
    },
    toggleYoursMenu(rowIdx) {
      if (this.disabled) return;
      this.directionOpenIdx = null;
      this.galleryOpenIdx = null;
      this.cardMenuIdx = null;
      this.yoursMenuIdx = this.yoursMenuIdx === rowIdx ? null : rowIdx;
    },
    toggleGallery(rowIdx) {
      if (this.disabled) return;
      this.directionOpenIdx = null;
      this.yoursMenuIdx = null;
      this.cardMenuIdx = null;
      this.galleryOpenIdx = this.galleryOpenIdx === rowIdx ? null : rowIdx;
    },
    onGalleryPick(rowIdx, tpl) {
      if (this.disabled) return;
      this.setRowTemplate(rowIdx, tpl);
      this.galleryOpenIdx = null;
    },
    requestOwnImage(rowIdx, source) {
      this.yoursMenuIdx = null;
      // The parent (NetworksPageV2) resolves the URL (upload / File Browser /
      // Salsify) and writes it back via the `ownImageResult` prop. (POR-13271)
      this.$emit('own-image-request', { rowIndex: this.localRows[rowIdx].rowIndex, source });
    },
    // Switch a card's mode from a toolbar button; closes any open popover so the
    // new selection isn't hidden behind one. (POR-13766)
    pickMode(rowIdx, mode) {
      this.galleryOpenIdx = null;
      this.yoursMenuIdx = null;
      this.directionOpenIdx = null;
      this.cardMenuIdx = null;
      this.setRowMode(rowIdx, mode);
    },
    togglePresetDropdown() {
      if (this.disabled) return;
      this.presetOpen = !this.presetOpen;
    },
    toggleAddableTypesDropdown() {
      if (this.disabled) {
        return;
      }

      this.addableTypesOpen = !this.addableTypesOpen;
    },
    toggleCreateMenu() {
      if (this.disabled) return;
      this.createMenuOpen = !this.createMenuOpen;
    },
    chooseCreate(mode) {
      this.createMenuOpen = false;
      this.confirm(mode);
    },
    onDocumentMousedown(event) {
      const inside = this.$el && this.$el.contains(event.target);
      if (!inside) {
        this.presetOpen = false;
        this.addableTypesOpen = false;
        this.createMenuOpen = false;
        this.directionOpenIdx = null;
        this.yoursMenuIdx = null;
        this.galleryOpenIdx = null;
        this.cardMenuIdx = null;
        return;
      }
      // Click inside the widget but outside an open popover (direction wand, yours
      // menu, gallery picker) or its trigger toolbar closes those poppers. The
      // triggers and popovers themselves are whitelisted so their own mousedown
      // doesn't close them before the click toggles. (POR-13271 / POR-13766)
      const keep = event.target.closest && event.target.closest(
        '.pdp-set-picker__direction, .pdp-set-picker__card-tools-right, .pdp-set-picker__card-tools, .pdp-set-picker__yours-menu, .pdp-set-picker__card-upload, .pdp-set-picker__gallery-pop, .pdp-set-picker__card-menu-pop',
      );
      if (!keep) {
        this.directionOpenIdx = null;
        this.yoursMenuIdx = null;
        this.galleryOpenIdx = null;
        this.cardMenuIdx = null;
      }

      const inAddArea = event.target.closest && event.target.closest(
        '.pdp-set-picker__add-trigger, .pdp-set-picker__add-dropdown-menu',
      );

      if (!inAddArea) {
        this.addableTypesOpen = false;
      }

      const inCreateSplit = event.target.closest && event.target.closest('.pdp-set-picker__create-split');
      if (!inCreateSplit) {
        this.createMenuOpen = false;
      }
    },
    onDocumentKeydown(event) {
      if (event.key === 'Escape') {
        this.presetOpen = false;
        this.addableTypesOpen = false;
        this.createMenuOpen = false;
        this.directionOpenIdx = null;
        this.yoursMenuIdx = null;
        this.galleryOpenIdx = null;
        this.cardMenuIdx = null;
      }
    },
    selectPreset(id) {
      this.activePresetId = id;
      this.presetOpen = false;
      const src = this.presetSources.find((s) => s.id === id);
      if (!src) return;
      const newRows = (src.slots || []).map((slot, i) => ({
        rowIndex: i,
        _uid: nextRowUid(),
        type: slot.key,
        // Library key before resolution — carries the company's configured
        // creative direction into the prompt. (POR-14039)
        libraryKey: slot.libraryKey || slot.key,
        label: slot.label,
        // Slots saved as "Upload" in the Library open waiting for the user's own
        // image instead of generating one. Still overridable here. (POR-14020)
        mode: slot.source === 'upload' ? 'yours' : 'ai',
        template_id: null,
        template_thumb: null,
        scene: null,
        productMode: null,
        overlayTextMode: DEFAULT_OVERLAY_TEXT_MODE,
      }));
      this.resetRowMaps();
      this.localRows = newRows.map((r) => this.withHeroMode(r));
    },
    setAll(mode) {
      if (mode === 'ai') {
        this.localRows = this.localRows.map((r) => (this.isHeroRow(r) ? r : {
          ...r,
          mode: 'ai',
          template_id: null,
          template_thumb: null,
          scene: null,
        }));
        return;
      }
      if (mode === 'gallery') {
        this.localRows = this.localRows.map((r) => {
          if (this.isHeroRow(r)) return r;
          const tpls = this.templatesForRow(r);
          if (!tpls.length) return r;
          const first = tpls[0];
          return {
            ...r,
            mode: 'gallery',
            template_id: first.id,
            template_thumb: first.thumbnail,
            template_image_url: first.image_url || first.thumbnail || null,
            scene: null,
          };
        });
      }
    },
    setRowMode(rowIdx, mode) {
      const row = { ...this.localRows[rowIdx], mode };
      if (mode !== 'gallery') {
        row.template_id = null;
        row.template_thumb = null;
        row.template_image_url = null;
      }
      if (mode !== 'scene') {
        row.scene = null;
      }
      if (mode === 'original') {
        // Switching a hero back to "Original" must restore the use-as-is packshot.
        row.original_image_url = row.original_image_url || this.productImageUrl || null;
      }
      this.$set(this.localRows, rowIdx, row);
    },
    setRowTemplate(rowIdx, tpl) {
      const row = {
        ...this.localRows[rowIdx],
        mode: 'gallery',
        template_id: tpl.id,
        template_thumb: tpl.thumbnail,
        template_image_url: tpl.image_url || tpl.thumbnail || null,
        scene: null,
      };
      this.$set(this.localRows, rowIdx, row);
    },
    onTemplateTileClick(rowIdx, tpl) {
      if (this.disabled) return;
      this.setRowTemplate(rowIdx, tpl);
    },
    addRow(typeDef) {
      const newRow = {
        rowIndex: this.localRows.length,
        _uid: nextRowUid(),
        type: typeDef.key,
        label: typeDef.label,
        mode: 'ai',
        template_id: null,
        template_thumb: null,
        scene: null,
        productMode: null,
        overlayTextMode: DEFAULT_OVERLAY_TEXT_MODE,
      };
      this.localRows = reindexRowSelections([...this.localRows, this.withHeroMode(newRow)]);

      this.$nextTick(() => {
        const anchor = this.$refs.footer;

        if (!anchor) {
          return;
        }

        anchor.style.scrollMarginBottom = '120px';

        anchor.scrollIntoView({ behavior: 'smooth', block: 'end' });
      });
    },
    toggleCardMenu(rowIdx, event) {
      if (this.disabled) return;
      this.directionOpenIdx = null;
      this.yoursMenuIdx = null;
      this.galleryOpenIdx = null;
      this.cardMenuIdx = this.cardMenuIdx === rowIdx ? null : rowIdx;

      if (this.cardMenuIdx === rowIdx && event) {
        const rect = event.currentTarget.getBoundingClientRect();

        // The menu is ~100px tall; flip it above the button when it would run past
        // the bottom of the viewport.
        this.$set(this.cardMenuAbove, rowIdx, window.innerHeight - rect.bottom < 140);
      }
    },
    // Clone a slot in place: same type and every customization, inserted directly to
    // the right of the source so the copy is obvious. (POR-13854)
    duplicateRow(rowIdx) {
      if (this.disabled) return;

      const src = this.localRows[rowIdx];

      this.cardMenuIdx = null;

      if (!src) {
        return;
      }

      const copy = {
        ...src,
        _uid: nextRowUid(),
        label: this.duplicateLabel(src),
      };

      // Copy the arrays by value — sharing them would make edits on the duplicate
      // mutate the original's product images.
      if (Array.isArray(src.productImageUrls)) {
        copy.productImageUrls = src.productImageUrls.slice();
      }

      if (Array.isArray(src.productImageNames)) {
        copy.productImageNames = src.productImageNames.slice();
      }

      const next = this.localRows.slice();

      next.splice(rowIdx + 1, 0, copy);
      this.shiftRowMaps({ insertAt: rowIdx + 1, cloneFrom: rowIdx });
      this.localRows = reindexRowSelections(next);
    },
    // "Elevated Hero" -> "Elevated Hero (2)", reusing any ordinal freed by a deleted
    // duplicate. The type itself is untouched, so only the badge and the layout template
    // name differ. (POR-13854)
    duplicateLabel(row) {
      const sameTypeLabels = this.localRows
        .filter((r) => r.type === row.type)
        .map((r) => r.label);

      return nextDuplicateLabel(row.label, sameTypeLabels);
    },
    removeRowFromMenu(rowIdx) {
      this.cardMenuIdx = null;
      this.removeRow(rowIdx);
    },
    // The per-tile side maps below are keyed by array index, so every splice of
    // localRows has to remap them or a card's brand logo / product images end up on
    // its neighbour. (POR-13854)
    shiftRowMaps(op) {
      this.brandLogoByRowIdx = remapIndexKeys(this.brandLogoByRowIdx, op);
      this.productImageByRowIdx = remapIndexKeys(this.productImageByRowIdx, op);
      this.activeRefTab = remapIndexKeys(this.activeRefTab, op);
      this.directionAbove = remapIndexKeys(this.directionAbove, op);
      this.directionLeftAlign = remapIndexKeys(this.directionLeftAlign, op);
      this.cardMenuAbove = remapIndexKeys(this.cardMenuAbove, op);

      const nextApplyAllRowIdx = shiftScalarIndex(this.productImageApplyAllRowIdx, op);

      this.productImageApplyAllRowIdx = nextApplyAllRowIdx;

      // The "use for all images" owner row is gone — drop the set-wide default too.
      if (nextApplyAllRowIdx === null) {
        this.productImageApplyAll = false;
        this.productImageApplyAllValue = null;
      }

      // pendingLogoRowIdx / pendingProductRowIdx (and their *DirectionIdx pairs) are
      // deliberately NOT remapped: they only live while a blocking modal or file dialog
      // covers the picker, so no add/remove/reorder can happen before the result lands.
      // If either resolver ever becomes non-blocking, they must be shifted here. (POR-13854)
    },
    // Switching preset replaces every row, so no side map entry is still meaningful.
    resetRowMaps() {
      this.brandLogoByRowIdx = {};
      this.productImageByRowIdx = {};
      this.activeRefTab = {};
      this.directionAbove = {};
      this.directionLeftAlign = {};
      this.cardMenuAbove = {};
      this.productImageApplyAll = false;
      this.productImageApplyAllRowIdx = null;
      this.productImageApplyAllValue = null;
    },
    removeRow(rowIdx) {
      if (this.disabled) return;
      const next = this.localRows.slice();
      next.splice(rowIdx, 1);
      this.shiftRowMaps({ removeAt: rowIdx });
      this.localRows = reindexRowSelections(next);
    },
    initSortable() {
      const el = this.$refs.cardsList;

      if (!el) {
        return;
      }

      if (this.sortable) {
        this.sortable.destroy();
      }

      this.sortable = Sortable.create(el, {
        handle: '.pdp-set-picker__card-body',
        draggable: '.pdp-set-picker__card',
        // The empty-upload prompt sits inside the drag handle — exclude it so a
        // click opens the "Yours" menu instead of starting a drag. (POR-14020)
        filter: '.pdp-set-picker__card-upload',
        preventOnFilter: false,
        animation: 150,
        disabled: this.disabled,
        onEnd: this.onRowSortEnd,
      });
    },
    onRowSortEnd({ oldIndex, newIndex }) {
      if (this.disabled || oldIndex === newIndex || oldIndex == null || newIndex == null) {
        return;
      }

      const next = this.localRows.slice();
      const [moved] = next.splice(oldIndex, 1);
      next.splice(newIndex, 0, moved);
      this.shiftRowMaps({ from: oldIndex, to: newIndex });
      this.localRows = reindexRowSelections(next);
    },
    openLightbox(rowIdx, tpl) {
      const row = this.localRows[rowIdx];
      this.lightbox = {
        visible: true,
        rowIdx,
        template: tpl,
        isSelected: row.mode === 'gallery' && row.template_id === tpl.id,
      };
    },
    closeLightbox() {
      this.lightbox = { visible: false, rowIdx: null, template: null, isSelected: false };
    },
    onLightboxSelect(tpl) {
      if (this.lightbox.rowIdx !== null) this.setRowTemplate(this.lightbox.rowIdx, tpl);
      this.galleryOpenIdx = null;
      this.closeLightbox();
    },
    onLightboxDeselect() {
      if (this.lightbox.rowIdx !== null) this.setRowMode(this.lightbox.rowIdx, 'ai');
      this.closeLightbox();
    },
    openImagePreview(url, label) {
      if (!url) return;
      this.imagePreview = { visible: true, url, label: label || '' };
    },
    closeImagePreviewModal() {
      this.imagePreview = { visible: false, url: '', label: '' };
    },
    confirm(mode) {
      const generationMode = mode || this.generationMode || 'parallel';
      try {
        if (window.localStorage) window.localStorage.setItem('cs_pdp_generation_mode', generationMode);
      } catch (e) { /* ignore */ }

      const rowsWithExtras = this.localRows.map((r, i) => {
        const logo = this.brandLogoByRowIdx[i];
        // Per-tile product images win; otherwise inherit the "use for all" default.
        const own = this.productImagesFor(i);
        const applyAll = (this.productImageApplyAll && Array.isArray(this.productImageApplyAllValue))
          ? this.productImageApplyAllValue
          : [];
        const productImages = own.length ? own : applyAll;
        let row = r;
        if (logo) {
          row = { ...row, brandLogoUrl: logo.url, brandLogoName: logo.name };
        }
        if (productImages.length) {
          // Keep the singular fields for existing single-product consumers and add
          // the full list for types that composite several. (POR-13766-4)
          row = {
            ...row,
            productImageUrl: productImages[0].url,
            productImageName: productImages[0].name || '',
            productImageUrls: productImages.map((im) => im.url),
            productImageNames: productImages.map((im) => im.name || ''),
          };
        }
        return row;
      });

      this.$emit('confirm', {
        rowSelections: reindexRowSelections(rowsWithExtras),
        presetId: this.activePresetId,
        generationMode,
        size: this.selectedSize,
      });
    },
    selectSize(size) {
      if (this.disabled) return;
      this.selectedSize = size;
    },
  },
};
</script>

<style lang="scss" scoped>
.pdp-set-picker {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
  font-size: 13px;
}
.pdp-set-picker__header {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
/* Title and the bulk mode actions share one row so the controls above the
   cards take two rows instead of three. (POR-14023) */
.pdp-set-picker__header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}
.pdp-set-picker__title {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
}
.pdp-set-picker__subtitle {
  color: #475569;
  margin: 0;
  line-height: 1.5;
}
.pdp-set-picker__intro-icon {
  color: #1a1a2e;
  margin-right: 1px;
  font-size: 0.95em;
}

/* Top control row: sequence preset OR reference input. */
.pdp-set-picker__controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.pdp-set-picker__preset {
  position: relative;
  flex: 0 0 auto;
  min-width: 240px;
}
.pdp-set-picker__preset-trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 14px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
}
.pdp-set-picker__preset-icon { color: #379C75; flex-shrink: 0; }
.pdp-set-picker__preset-value { flex: 1; color: #94a3b8; font-size: 13px; }
.pdp-set-picker__preset-chevron { color: #94a3b8; flex-shrink: 0; }
.pdp-set-picker__preset-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
  max-height: 240px;
  overflow-y: auto;
  z-index: 30;
}
.pdp-set-picker__preset-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 14px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
}
.pdp-set-picker__preset-item:hover { background: #f1f5f9; }
.pdp-set-picker__preset-item.is-active { background: #eaf7f2; color: #2d6a55; }
.pdp-set-picker__preset-count { color: #94a3b8; font-weight: 400; }
.pdp-set-picker__or { color: #94a3b8; font-size: 12px; flex-shrink: 0; }
.pdp-set-picker__reference {
  position: relative;
  flex: 1 1 260px;
  min-width: 240px;
  display: flex;
  align-items: center;
}
.pdp-set-picker__reference-icon {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  font-size: 13px;
  pointer-events: none;
}
.pdp-set-picker__reference-input {
  width: 100%;
  padding: 10px 14px 10px 32px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  color: #334155;
  background: #fff;
}
.pdp-set-picker__reference-input:focus {
  outline: none;
  border-color: #379C75;
}
.pdp-set-picker__reference-input:disabled { opacity: 0.6; cursor: not-allowed; }

/* Brief entry point — pushed to the far end of the toolbar row. */
.pdp-set-picker__brief {
  display: flex;
  /* stretch, not center: the open-icon button takes its height from the button
     beside it instead of carrying its own. */
  align-items: stretch;
  gap: 6px;
  flex-shrink: 0;
  margin-left: auto;
}
.pdp-set-picker__brief-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  max-width: 260px;
  padding: 10px 14px;
  border: 1px solid #cfe7dd;
  border-radius: 8px;
  background: #eaf7f2;
  color: #2d6a55;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.pdp-set-picker__brief-btn:hover { background: #ddf0e8; border-color: #b6dbcc; }
.pdp-set-picker__brief-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pdp-set-picker__brief-open {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #64748b;
  font-size: 15px;
  cursor: pointer;
}
.pdp-set-picker__brief-open:hover { background: #f1f5f9; color: #2d6a55; }

.pdp-set-picker__bulk {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}
.pdp-set-picker__bulk-sep { color: #cbd5e1; }
.pdp-set-picker__bulk-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 4px;
  border: none;
  background: transparent;
  color: #475569;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
}
.pdp-set-picker__bulk-btn i { color: #379C75; }
.pdp-set-picker__bulk-btn:hover:not(:disabled) { color: #1a1a2e; }
.pdp-set-picker__bulk-btn:disabled { opacity: 0.5; cursor: not-allowed; }

/* Card grid — fills the (widened) content column; wraps at ~4 up. */
.pdp-set-picker__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 14px;
}
.pdp-set-picker__card {
  position: relative;
  aspect-ratio: 1 / 0.92;
  min-height: 190px;
  border-radius: 10px;
}
.pdp-set-picker__card.is-popover-open { z-index: 50; }
.pdp-set-picker__card-frame {
  position: absolute;
  inset: 0;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #dbe4e0;
  background: #e6efeb;
}
.pdp-set-picker__card-body {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
}
.pdp-set-picker__card-body:active { cursor: grabbing; }
.pdp-set-picker__card-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}
.pdp-set-picker__card-spark {
  font-size: 46px;
  color: #7fb5a2;
  pointer-events: none;
}
/* Empty "Yours" card — asks for the image the sequence said the user supplies. */
.pdp-set-picker__card-upload {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 14px 12px;
  border: 1px dashed #b6c4cf;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.7);
  color: #64748b;
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.2;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.12s, color 0.12s, background 0.12s;
}
.pdp-set-picker__card-upload i {
  font-size: 26px;
}
.pdp-set-picker__card-upload:hover:not(:disabled) {
  border-color: #379c75;
  background: #fff;
  color: #379c75;
}
.pdp-set-picker__card-upload:disabled {
  cursor: default;
  opacity: 0.6;
}
.pdp-set-picker__card-label {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 18px 12px 8px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(to top, rgba(15, 23, 42, 0.78), rgba(15, 23, 42, 0));
  pointer-events: none;
}
.pdp-set-picker__card-ref-badge {
  position: absolute;
  left: 8px;
  bottom: 34px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 7px;
  border-radius: 10px;
  background: rgba(45, 106, 85, 0.92);
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  z-index: 3;
}
/* Sequential flow: this row's image is already generated and kept. (POR-13869) */
.pdp-set-picker__card-done {
  position: absolute;
  top: 6px;
  right: 6px;
  z-index: 2;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #379C75;
  color: #fff;
  font-size: 12px;
  line-height: 1;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.25);
}
/* Bootstrap Icons ship `vertical-align: -.125em` on every `bi-*` and the glyph
   carries its own line-height, so flex centering alone still leaves the check
   sitting low in the circle. Neutralise both on the icon itself. (POR-13869) */
.pdp-set-picker__card-done > i {
  display: block;
  line-height: 1;
  vertical-align: 0;
}
/* Left toolbar (mode) + right toolbar (wand / remove): revealed on hover. */
.pdp-set-picker__card-tools {
  position: absolute;
  top: 8px;
  left: 8px;
  display: flex;
  gap: 6px;
  opacity: 0;
  transition: opacity 0.15s;
  z-index: 6;
}
.pdp-set-picker__card-tools-right {
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  gap: 6px;
  opacity: 0;
  transition: opacity 0.15s;
  z-index: 6;
}
.pdp-set-picker__card:hover .pdp-set-picker__card-tools,
.pdp-set-picker__card:hover .pdp-set-picker__card-tools-right,
.pdp-set-picker__card.is-popover-open .pdp-set-picker__card-tools,
.pdp-set-picker__card.is-popover-open .pdp-set-picker__card-tools-right {
  opacity: 1;
}
.pdp-set-picker__mode-btn {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.18);
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.pdp-set-picker__mode-btn:hover:not(:disabled) {
  border-color: #379C75;
  color: #2d6a55;
}
.pdp-set-picker__mode-btn.is-active {
  background: #379C75;
  border-color: #379C75;
  color: #fff;
}
.pdp-set-picker__mode-btn.is-open {
  border-color: #1ca9e3;
  box-shadow: 0 0 0 2px rgba(28, 169, 227, 0.25);
}
.pdp-set-picker__mode-btn:disabled { cursor: not-allowed; opacity: 0.5; }
.pdp-set-picker__card-expand {
  position: absolute;
  bottom: 8px;
  right: 8px;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  color: #334155;
  display: none;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  cursor: pointer;
  z-index: 5;
}
.pdp-set-picker__card:hover .pdp-set-picker__card-expand { display: inline-flex; }

.pdp-set-picker__wand {
  position: relative;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.18);
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
}
/* Has direction: light-green "partly filled" tint + dot. (POR-13271 feedback) */
.pdp-set-picker__wand.is-active {
  background: #eaf7f2;
  border-color: #379C75;
  color: #2d6a55;
}
.pdp-set-picker__wand:hover:not(:disabled) {
  background: #379C75;
  border-color: #379C75;
  color: #fff;
}
.pdp-set-picker__wand.is-open {
  border-color: #1ca9e3;
  box-shadow: 0 0 0 2px rgba(28, 169, 227, 0.25);
}
.pdp-set-picker__wand:disabled { cursor: not-allowed; }
.pdp-set-picker__wand-dot {
  position: absolute;
  top: -3px;
  right: -3px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  border: 1.5px solid #fff;
}

/* Gallery template popover. */
.pdp-set-picker__gallery-pop {
  position: absolute;
  top: 46px;
  left: 8px;
  width: 300px;
  max-width: calc(100vw - 40px);
  z-index: 45;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.16);
  padding: 10px;
}
.pdp-set-picker__gallery-pop-head {
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 8px;
}
.pdp-set-picker__gallery-pop-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  max-height: 220px;
  overflow-y: auto;
}
.pdp-set-picker__gallery-pop-tile {
  position: relative;
  aspect-ratio: 1 / 1;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
  cursor: pointer;
  background: #f8fafc;
}
.pdp-set-picker__gallery-pop-tile img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}
.pdp-set-picker__gallery-pop-tile.is-selected {
  border-color: #379C75;
  border-width: 2px;
}
.pdp-set-picker__gallery-pop-check {
  position: absolute;
  top: 3px;
  right: 3px;
  color: #379C75;
  background: #fff;
  border-radius: 50%;
  font-size: 15px;
  line-height: 1;
}
.pdp-set-picker__gallery-pop-expand {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 18px;
  height: 18px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.85);
  border: none;
  display: none;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  cursor: pointer;
}
.pdp-set-picker__gallery-pop-tile:hover .pdp-set-picker__gallery-pop-expand { display: flex; }
.pdp-set-picker__gallery-pop-browse {
  margin-top: 8px;
  background: transparent;
  border: none;
  padding: 4px 0 0;
  color: #379C75;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.pdp-set-picker__gallery-pop-browse:hover { text-decoration: underline; }

/* Yours menu. */
.pdp-set-picker__yours-menu {
  position: absolute;
  top: 46px;
  left: 8px;
  width: 240px;
  max-width: calc(100vw - 40px);
  z-index: 45;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.16);
  padding: 6px;
}
.pdp-set-picker__yours-menu-head {
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.04em;
  padding: 8px 10px 4px;
}
.pdp-set-picker__yours-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 10px;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #334155;
  text-align: left;
}
.pdp-set-picker__yours-menu-item:hover { background: #f1f5f9; }
.pdp-set-picker__yours-menu-item:disabled { opacity: 0.5; cursor: not-allowed; }
.pdp-set-picker__yours-menu-item i { color: #64748b; }

/* 3-dot "More actions" button + menu (POR-13854). */
.pdp-set-picker__card-menu {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.18);
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  padding: 0;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.pdp-set-picker__card-menu:hover:not(:disabled),
.pdp-set-picker__card-menu.is-open {
  background: #379C75;
  border-color: #379C75;
  color: #fff;
}
.pdp-set-picker__card-menu:disabled { cursor: not-allowed; opacity: 0.5; }
.pdp-set-picker__card-menu-pop {
  position: absolute;
  top: 46px;
  right: 8px;
  width: 210px;
  max-width: calc(100vw - 40px);
  z-index: 45;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.16);
  padding: 6px;
  cursor: default;
}
.pdp-set-picker__card-menu-pop.is-above {
  top: auto;
  bottom: 46px;
}
.pdp-set-picker__card-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 10px;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  color: #334155;
  text-align: left;
  transition: background 0.15s, color 0.15s;
}
.pdp-set-picker__card-menu-item i { color: #64748b; }
.pdp-set-picker__card-menu-item:hover:not(:disabled) { background: #379C75; color: #fff; }
.pdp-set-picker__card-menu-item:hover:not(:disabled) i { color: #fff; }
.pdp-set-picker__card-menu-item.is-danger { color: #dc2626; }
.pdp-set-picker__card-menu-item.is-danger i { color: #dc2626; }
.pdp-set-picker__card-menu-item.is-danger:hover:not(:disabled) { background: #fee2e2; color: #b91c1c; }
.pdp-set-picker__card-menu-item.is-danger:hover:not(:disabled) i { color: #b91c1c; }
.pdp-set-picker__card-menu-item:disabled { opacity: 0.5; cursor: not-allowed; }

/* Direction (wand) popover. */
.pdp-set-picker__direction {
  position: absolute;
  top: 46px;
  right: 8px;
  width: 385px;
  max-width: calc(100vw - 40px);
  z-index: 45;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 5px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.16);
  padding: 14px;
  cursor: default;
}
.pdp-set-picker__direction.is-above {
  top: auto;
  bottom: 46px;
}
.pdp-set-picker__direction.is-left {
  right: auto;
  left: 8px;
}
.pdp-set-picker__direction-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
}
.pdp-set-picker__direction-title {
  font-weight: 700;
  font-size: 12px;
  color: #1a1a2e;
  font-family: ui-sans-serif, system-ui, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji';
}
.pdp-set-picker__direction-optional { color: #64748b; font-size: 10px; }
.pdp-set-picker__direction-text {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 5px;
  padding: 8px 10px;
  font-size: 13px;
  resize: vertical;
  color: #334155;
  font-family: inherit;
  background: #f8fafc;
}
.pdp-set-picker__direction-text:focus {
  outline: none;
  border: 2px solid #189a73;
}
.pdp-set-picker__direction-foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
}
.pdp-set-picker__direction-note { color: #94a3b8; font-size: 10px; }
.pdp-set-picker__direction-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #1a1a2e;
  font-family: ui-sans-serif, system-ui, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji';
}
.pdp-set-picker__direction-section {
  margin-top: 14px;
}
.pdp-set-picker__direction-section-head {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: #1a1a2e;
  font-size: 13px;
  margin-bottom: 8px;
}
.pdp-set-picker__logo-btn {
  width: 100%;
  height: 36px;
  padding: 10px;
  border: 1px dashed #cbd5e1;
  border-radius: 4px;
  background: #f8fafc;
  color: #64748b;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: border-color 0.15s, background 0.15s;
}
.pdp-set-picker__logo-btn:hover:not(:disabled) {
  color: #0f172a;
  background: #189a73;
}
.pdp-set-picker__logo-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.pdp-set-picker__logo-remove {
  margin-left: auto;
  background: none;
  border: none;
  color: #64748b;
  font-size: 10px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
}
.pdp-set-picker__logo-remove:hover { color: #ef4444; }
.pdp-set-picker__logo-selected {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
}
/* Stacked product images: one row per picked packshot. (POR-13766-4) */
.pdp-set-picker__product-item + .pdp-set-picker__product-item {
  margin-top: 6px;
}
.pdp-set-picker__product-item {
  margin-bottom: 6px;
}
.pdp-set-picker__logo-thumb-sm {
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 4px;
  flex-shrink: 0;
}
.pdp-set-picker__logo-name {
  flex: 1;
  font-size: 12px;
  color: #334155;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.pdp-set-picker__logo-change {
  background: none;
  border: none;
  color: #379C75;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
}
.pdp-set-picker__logo-change:hover { text-decoration: underline; }
.pdp-set-picker__logo-change:disabled { opacity: 0.5; cursor: not-allowed; }
.pdp-set-picker__ref-tabs {
  display: inline-flex;
  gap: 4px;
  padding: 3px;
  background: #f1f5f9;
  border-radius: 8px;
  margin-bottom: 10px;
}
.pdp-set-picker__ref-tab {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}
.pdp-set-picker__ref-tab.is-active {
  background: #fff;
  color: #1a1a2e;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
}
.pdp-set-picker__ref-tab:disabled { opacity: 0.5; cursor: not-allowed; }
.pdp-set-picker__ref-tab-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #22c55e;
  flex-shrink: 0;
}
.pdp-set-picker__ref-dropzone {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0 4px;
  padding: 10px;
  border: 1px dashed #cbd5e1;
  border-radius: 4px;
  background: #f8fafc;
  font-size: 13px;
  color: #64748b;
  transition: border-color 0.15s, background 0.15s;
}
.pdp-set-picker__ref-dropzone.is-dragover {
  border-color: #379C75;
  background: #ecfdf5;
}
.pdp-set-picker__ref-link {
  background: none;
  border: none;
  padding: 0;
  color: #379C75;
  font-size: 13px;
  text-decoration: underline;
  cursor: pointer;
}
.pdp-set-picker__ref-link:hover:not(:disabled) { color: #2E8D68; }
.pdp-set-picker__ref-link:disabled { opacity: 0.5; cursor: not-allowed; }
.pdp-set-picker__ref-sep {
  margin-left: -3px;
  color: #64748b;
}
.pdp-set-picker__ref-remove {
  flex-shrink: 0;
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 11px;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  line-height: 1;
}
.pdp-set-picker__ref-remove:hover:not(:disabled) {
  color: #ef4444;
  background: #fef2f2;
}
.pdp-set-picker__ref-remove:disabled { opacity: 0.5; cursor: not-allowed; }
.pdp-set-picker__ref-all {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 10px;
  font-size: 12px;
  color: #475569;
  cursor: pointer;
  user-select: none;
}
.pdp-set-picker__ref-all input[type="checkbox"] {
  width: 15px;
  height: 15px;
  accent-color: #379C75;
  cursor: pointer;
  flex-shrink: 0;
}
.pdp-set-picker__ref-all input[type="checkbox"]:disabled { cursor: not-allowed; }
.pdp-set-picker__direction-done {
  background: #189a73;
  color: #fff;
  border: none;
  border-radius: 5px;
  padding: 5px 10px;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.pdp-set-picker__direction-done:hover { background: #189a73e6; }
.pdp-set-picker__overlay-mode {
  display: flex;
  gap: 4px;
  margin-top: 6px;
}
.pdp-set-picker__overlay-mode-btn {
  padding: 4px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #f8fafc;
  color: #475569;
  font-size: 11px;
  cursor: pointer;
  text-align: center;
}
.pdp-set-picker__overlay-mode-btn input[type="radio"] {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}
.pdp-set-picker__overlay-mode-btn.is-active {
  background: #1f8a6d;
  border-color: #1f8a6d;
  color: #fff;
}
.pdp-set-picker__overlay-mode-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.separator {
  border: 1px solid #f1f5f9;
  margin: 10px 0 0 0;
}

/* Add image type — pinned to the right of the "All AI · All Gallery" row. */
.pdp-set-picker__add-image-type {
  position: relative;
}
/* Only the LAST control on the row may claim the free space. Two `margin-left:
   auto` siblings split it between them, which on a narrow picker — where the
   row wraps and these two land on a line of their own — pushed this button off
   the left edge instead of leaving it flush. When no brief button follows, this
   one is last and keeps the original right alignment. */
.pdp-set-picker__add-image-type:last-child {
  margin-left: auto;
}
.pdp-set-picker__add-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  /* Same vertical padding as the preset trigger and the reference input, so
     every control on this row lands on one height. */
  padding: 10px 14px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #f8fafc;
  color: #64748b;
  font-size: 13px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.pdp-set-picker__add-trigger:hover:not(:disabled) {
  color: #0f172a;
  border: 1px dashed #cbd5e1;
  background: #189a73;
}
.pdp-set-picker__add-trigger.is-open {
  border-color: #94a3b8;
  background: #f1f5f9;
  color: #334155;
}
.pdp-set-picker__add-trigger:disabled { opacity: 0.5; cursor: not-allowed; }
.pdp-set-picker__add-chevron {
  font-size: 9px;
  margin-left: 2px;
  transition: transform 0.15s;
}
.pdp-set-picker__add-trigger.is-open .pdp-set-picker__add-chevron {
  transform: rotate(180deg);
}
.pdp-set-picker__add-dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
  min-width: 180px;
  max-height: fit-content;
  overflow-y: auto;
  z-index: 30;
  padding: 4px;
}
.pdp-set-picker__add-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 7px 12px;
  background: transparent;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 13px;
  color: #334155;
  text-align: left;
  white-space: nowrap;
}
.pdp-set-picker__add-item:hover { background: #f1f5f9; }

/* Footer. */
/* Inline output-size row, between the cards and the Create button. (POR-14023) */
.pdp-set-picker__size {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
}
.pdp-set-picker__size-label {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a2e;
  flex-shrink: 0;
}
.pdp-set-picker__size-options {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.pdp-set-picker__size-btn {
  padding: 6px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}
.pdp-set-picker__size-btn:hover:not(:disabled) {
  border-color: #379C75;
  color: #1a1a2e;
}
.pdp-set-picker__size-btn.is-active {
  background: #379C75;
  border-color: #379C75;
  color: #fff;
}
.pdp-set-picker__size-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.pdp-set-picker__size-btn.is-active:disabled { opacity: 0.8; }
.pdp-set-picker__size-hint {
  margin-left: auto;
  color: #94a3b8;
  font-size: 12.5px;
}

.pdp-set-picker__upload-hint {
  color: #94a3b8;
  font-size: 12.5px;
}

.pdp-set-picker__footer {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 8px;
}
.pdp-set-picker__create {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #379C75;
  border-color: #379C75;
  color: #fff;
  padding: 8px 14px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 13px;
}
.pdp-set-picker__create:hover:not(:disabled) {
  background: #2E8D68;
  border-color: #2E8D68;
}
/* Highlight only on click, not by default (POR-13596) */
.pdp-set-picker__create:active:not(:disabled) {
  box-shadow: 0 0 0 3px rgba(55, 156, 117, 0.25);
}
.pdp-set-picker__create i {
  font-size: 0.95em;
  line-height: 1;
}
/* Split "Create" button: primary action + caret dropdown (parallel / sequential). */
.pdp-set-picker__create-split {
  position: relative;
  display: inline-flex;
  align-items: stretch;
}
.pdp-set-picker__create-split .pdp-set-picker__create {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}
.pdp-set-picker__create-caret {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 9px;
  background: #379C75;
  border: none;
  border-left: 1px solid rgba(255, 255, 255, 0.28);
  border-top-right-radius: 8px;
  border-bottom-right-radius: 8px;
  color: #fff;
  font-size: 11px;
  cursor: pointer;
}
.pdp-set-picker__create-caret:hover:not(:disabled) { background: #2E8D68; }
.pdp-set-picker__create-caret:disabled { cursor: not-allowed; opacity: 0.5; }
.pdp-set-picker__create-caret .bi { transition: transform 0.15s; }
.pdp-set-picker__create-split.is-open .pdp-set-picker__create-caret .bi { transform: rotate(180deg); }
.pdp-set-picker__create-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  min-width: 100%;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
  padding: 4px;
  z-index: 40;
  white-space: nowrap;
}
.pdp-set-picker__create-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  text-align: left;
}
.pdp-set-picker__create-menu-item:hover:not(:disabled) { background: #f1f5f9; }
.pdp-set-picker__create-menu-item:disabled { cursor: not-allowed; opacity: 0.5; }
.pdp-set-picker__create-menu-item i { color: #379C75; }
.pdp-set-picker__browse {
  background: transparent;
  border: none;
  padding: 0;
  color: #379C75;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
}
.pdp-set-picker__browse:hover { text-decoration: underline; }
.pdp-set-picker__browse:disabled { cursor: not-allowed; opacity: 0.5; }

/* Full-screen image preview. */
.pdp-set-picker__preview-lightbox {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(15, 23, 42, 0.78);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.pdp-set-picker__preview-close {
  position: absolute;
  top: 18px;
  right: 22px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  font-size: 16px;
  cursor: pointer;
  color: #111827;
}
.pdp-set-picker__preview-img {
  display: block;
  max-width: 88vw;
  max-height: 88vh;
  width: auto;
  height: auto;
  border-radius: 8px;
}
</style>
